import {
  Button,
  CheckBox,
  InfoPopup,
  LinkText,
  Loader,
  LogoWithTitle,
} from 'components';
import { useInfoPopup } from 'hooks';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useMedia } from 'react-use';
import {
  AppleCalendarIcon,
  AppleLogo,
  OutlookCalendarIcon,
  Redirect,
  UsersIcon,
} from 'static';
import successCheck from 'static/animations/success-check.json';
import Step1 from 'static/images/step-1.png';
import Step2 from 'static/images/step-2.png';
import Step2German from 'static/images/step-2-de.png';
import Step3 from 'static/images/step-3.png';
import Step3German from 'static/images/step-3-de.png';
import Step4 from 'static/images/step-4.png';
import Step4German from 'static/images/step-4-de.png';
import {
  doLinkAppleCalendar,
  doUnlinkAppleCalendar,
} from 'store/actionCreators/profile';
import {
  getAppleCalendarConnectLoading,
  getAppleCalendarDisconnectLoading,
  getFullAuthUser,
} from 'store/selectors/auth';
import { getAvailableCommunities } from 'store/selectors/communities';
import { css } from 'styled-components';
import { breakpoints, verticalBreakpoints } from 'theme';

import { FIELDS } from './form';
import { useChangeAppleSyncEvents } from './hooks';
import {
  AfterConnectedWrapper,
  BeforeConnectedWrapper,
  CalendarsCard,
  CalendarTitle,
  ConnectButtonWrapper,
  ConnectionLabel,
  ListWrapper,
  LoaderWrapper,
  StepsBlock,
  StyledAnchorTag,
  Wrapper,
} from './styled';
import { List, NoCommunitiesBlock } from './styled';

type Props = {
  expanded?: boolean;
  setExpanded?: React.Dispatch<
    React.SetStateAction<{ google: boolean; apple: boolean }>
  >;
  isConnected?: boolean;
  getRowHeight?: (type: string) => string;
  settingsSaving: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  onCommunityBlockToogle: (type: string) => void;
};

const ApplceCalendar: React.FC<Props> = ({
  expanded,
  isConnected,
  getRowHeight,
  settingsSaving,
  isConnecting,
  isDisconnecting,
  onCommunityBlockToogle,
  setExpanded,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const communities = useSelector(getAvailableCommunities);
  const user = useSelector(getFullAuthUser);
  const { showUserGuidePopup, showInfoPopup } = useInfoPopup();
  const calendarConnecting = useSelector(getAppleCalendarConnectLoading);
  const calendarDisconnecting = useSelector(getAppleCalendarDisconnectLoading);
  const hasVerticalSpace = useMedia(`${verticalBreakpoints.md}`);

  const defaultValues = useMemo(() => {
    return user?.settings?.syncEventsAppleFromCommunities;
  }, [user, communities]);

  const connectedCommunityCalendars = useMemo(() => {
    return user.settings.appleCalendarUrl;
  }, [user.settings.appleCalendarUrl]);

  const isLanguageGerman = user.settings.language === 'de';
  const appleSupportPageUrl = isLanguageGerman
    ? 'https://gumb.app/support/gumb-mit-apple-ical-kalender-verbinden/'
    : 'https://gumb.app/en/support/connect-gumb-with-apple-calender/';
  const outlookSupportPageUrl = isLanguageGerman
    ? 'https://gumb.app/support/gumb-mit-outlook-kalender-verbinden/'
    : 'https://gumb.app/en/support/connect-gumb-with-outlook-calendar/';

  const { onSubmit, syncAppleFieldProps, setValue } = useChangeAppleSyncEvents(
    user?.id,
    {
      [FIELDS.SYNC_EVENTS_APPLE]: defaultValues,
    },
  );

  const isSmPlus = useMedia(breakpoints.smPlus);
  const isMdPlus = useMedia(breakpoints.mdPlus);
  const isDownSmPlus = useMedia(breakpoints.downSmPlus);
  const isAppleConnected = (user.settings.appleCalendarUrl?.length ?? 0) > 0;
  const stepBlockRef = useRef<HTMLOListElement>(null);

  const getRowHeightForApple = useCallback(
    (type) => {
      if (type === 'google') {
        if (!isConnected) {
          return (isDownSmPlus ? 130 : 180) + 'px';
        }
      } else {
        if (!isAppleConnected) {
          return (isDownSmPlus ? 190 : 180) + 'px';
        }
      }

      const isExpanded = expanded;
      const communitiesHeight = (communities.length || 8) * 60;
      const extraOffset = !isSmPlus ? 65 : 0;
      const mainBlockHeight = !isMdPlus ? 215 : 172;
      const stepsHeight = stepBlockRef.current
        ? stepBlockRef.current.clientHeight
        : 0;

      return (
        extraOffset +
        (isExpanded
          ? 142 + 60 + communitiesHeight + stepsHeight
          : mainBlockHeight) +
        'px'
      );
    },
    [isDownSmPlus, isSmPlus, isMdPlus, expanded, communities, isConnected],
  );

  const showPopup = () => {
    showUserGuidePopup({
      title: t('calendarConnection.appleConnectionPopup.title'),
      multiStep: [
        {
          image: Step1,
          message: t('calendarConnection.appleConnectionPopup.stepOne'),
        },
        {
          image: isLanguageGerman ? Step2German : Step2,
          message: t('calendarConnection.appleConnectionPopup.stepTwo'),
        },
        {
          image: isLanguageGerman ? Step3German : Step3,
          message: t('calendarConnection.appleConnectionPopup.stepThree'),
        },
        {
          image: isLanguageGerman ? Step4German : Step4,
          message: t('calendarConnection.appleConnectionPopup.stepFour'),
        },
      ],
      buttonText: t('common.ok'),
      cancelButtonText: t('common.cancel'),
      messageAlign: 'center',
    });
  };

  const handleConnectionInitiate = async () => {
    setValue(
      FIELDS.SYNC_EVENTS_APPLE,
      communities.map((community) => community.id),
    );

    dispatch(
      doLinkAppleCalendar({
        userId: user.id,
        data: { [FIELDS.SYNC_EVENTS_APPLE]: communities.map((c) => c.id) },
        onSuccess: () => {
          setExpanded((prev) => ({ ...prev, apple: true }));
          showPopup();
        },
      }),
    );
  };

  const handleDisconnect = () => {
    setValue(FIELDS.SYNC_EVENTS_APPLE, []);
    dispatch(
      doUnlinkAppleCalendar({
        userId: user.id,
        data: { [FIELDS.SYNC_EVENTS_APPLE]: [] },
      }),
    );
  };

  const getCalendarUrl = (communityId: string) => {
    return connectedCommunityCalendars.find(
      (cal) => cal.communityId === communityId,
    )?.['url'];
  };

  const handleChange = (fieldName: string, communityId: string) => {
    const currentValue = syncAppleFieldProps.value;

    syncAppleFieldProps.onChange(
      currentValue.includes(communityId)
        ? currentValue.filter((i) => i !== communityId)
        : currentValue.concat([communityId]),
    );

    onSubmit();

    if (syncAppleFieldProps.value.length === 0)
      setExpanded((prev) => ({ ...prev, apple: false }));
  };

  const onCheckBoxChange = (communityId: string) => {
    if (syncAppleFieldProps.value.includes(communityId)) {
      showInfoPopup({
        title: t('calendarConnection.appleUncheckBoxPopup.title'),
        richMessage: t('calendarConnection.appleUncheckBoxPopup.message'),
        buttonText: t('calendarConnection.appleUncheckBoxPopup.okButton'),
        messageAlign: 'left',
        size: 'lg',
        type: 'warning',
        onButtonClick: () =>
          handleChange(FIELDS.SYNC_EVENTS_APPLE, communityId),
      });
      return;
    }
    handleChange(FIELDS.SYNC_EVENTS_APPLE, communityId);
  };

  const buttonStyle = css`
    text-transform: none;
    font-size: 14px;
    font-family: 'open sans';
    width: 100%;
    color: ${(props) => props.theme.color.pickledBluewood};
    background: white;
    box-shadow: 0 1px 3px 1px #00000036;
    border-radius: 4px;
    font-weight: 600;
    transition: all 0.2s ease;
    border: none;
    height: 40px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      background: #f8f9fa;
      box-shadow: 0 2px 6px 2px #00000040;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 1px 3px 1px #00000036;
    }
  `;

  const comminuteTitleStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    width: 'calc(100% - 28px)',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    minWidth: '30px',
  };

  return (
    <>
      <CalendarsCard>
        <CalendarsCard.Row
          expanded={expanded}
          style={{
            height: getRowHeightForApple('apple'),
            justifyContent: !isConnected ? 'center' : 'flex-start',
          }}
        >
          <CalendarsCard.Row.Main>
            {!isConnected ? (
              <BeforeConnectedWrapper>
                <Wrapper>
                  <CalendarTitle style={{ padding: '20px 0' }}>
                    <AppleCalendarIcon />
                    <span>{t('calendarConnection.appleCalendar')}</span>
                  </CalendarTitle>
                  <CalendarTitle style={{ padding: '20px 0' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '46px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 3,
                      }}
                    >
                      <OutlookCalendarIcon width={38} height={38} />
                    </div>
                    <span>{t('calendarConnection.outlookCalendar')}</span>
                  </CalendarTitle>
                </Wrapper>
                {calendarConnecting ? (
                  <LoaderWrapper width="240px">
                    <Loader type="button" size="28px" thickness={2} />
                  </LoaderWrapper>
                ) : (
                  <ConnectButtonWrapper>
                    <Button
                      width="max-content"
                      variant="primary"
                      // IconComponent={renderAppleCalendarIcon}
                      onClick={handleConnectionInitiate}
                      {...{ rounded: true }}
                      buttonStyles={buttonStyle}
                    >
                      <p>{t('calendarConnection.connectWithApple')}</p>
                    </Button>
                  </ConnectButtonWrapper>
                )}
              </BeforeConnectedWrapper>
            ) : (
              <AfterConnectedWrapper>
                <Wrapper>
                  <CalendarTitle style={{ padding: '20px 0' }}>
                    <AppleCalendarIcon />
                    <span>{t('calendarConnection.appleCalendar')}</span>
                  </CalendarTitle>
                  <CalendarTitle style={{ padding: '20px 0' }}>
                    <OutlookCalendarIcon width={38} height={46} />
                    <span>{t('calendarConnection.outlookCalendar')}</span>
                  </CalendarTitle>
                </Wrapper>
                <div>
                  <span onClick={() => onCommunityBlockToogle('apple')}>
                    {`${t('calendarConnection.selectCommunities')} (${
                      syncAppleFieldProps.value.length
                    }/${communities.length})`}
                  </span>
                </div>
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-evenly',
                      alignItems: 'center',
                    }}
                  >
                    <Lottie
                      options={{
                        animationData: successCheck,
                        loop: false,
                        autoplay: true,
                      }}
                      width="40px"
                      height="40px"
                    />
                    {t('calendarConnection.connected')}
                  </div>
                </div>
                <InfoPopup
                  title={t('calendarConnection.appleDisconnectPopup.title')}
                  richMessage={t(
                    'calendarConnection.appleDisconnectPopup.message',
                  )}
                  messageAlign="left"
                  type="warning"
                  buttonText={t(
                    'calendarConnection.appleDisconnectPopup.okButton',
                  )}
                  cancelButtonText={t('common.cancel')}
                  size={hasVerticalSpace ? 'lg' : 'md'}
                  onButtonClick={handleDisconnect}
                >
                  {calendarDisconnecting ? (
                    <LoaderWrapper width="120px">
                      <Loader type="button" size="28px" thickness={2} />
                    </LoaderWrapper>
                  ) : (
                    <ConnectionLabel>
                      {t('calendarConnection.disconnect')}
                    </ConnectionLabel>
                  )}
                </InfoPopup>
              </AfterConnectedWrapper>
            )}
          </CalendarsCard.Row.Main>

          {isConnected && (
            <StepsBlock ref={stepBlockRef}>
              <StepsBlock.Item
                fontSize="16px"
                marginBottom="8px"
                highlight
                borderRadius="4px"
                marginTop="0px"
              >
                <div style={{ fontWeight: 'bold' }}>
                  {t('calendarConnection.appleConnectionSteps.headerOne')}
                </div>
                <div style={{ marginTop: '4px' }}>
                  {t('calendarConnection.appleConnectionSteps.demoLinks')}{' '}
                  <strong>
                    {t(
                      'calendarConnection.appleConnectionSteps.demoLinksApple',
                    )}
                  </strong>
                  <StyledAnchorTag
                    display="inline-flex"
                    marginLeft="5px"
                    onClick={showPopup}
                    marginRight="5px"
                  >
                    {t(
                      'calendarConnection.appleConnectionSteps.demoLinksAppleHere',
                    )}
                  </StyledAnchorTag>
                  {'- '}
                  {t(
                    'calendarConnection.appleConnectionSteps.demoLinksDetailed',
                  )}{' '}
                  <strong>
                    {t(
                      'calendarConnection.appleConnectionSteps.demoLinksDetailedApple',
                    )}
                  </strong>
                  <StyledAnchorTag
                    display="inline-flex"
                    marginLeft="5px"
                    target="_blank"
                    href={appleSupportPageUrl}
                  >
                    {t(
                      'calendarConnection.appleConnectionSteps.demoLinksDetailedHere',
                    )}
                    <Redirect />
                  </StyledAnchorTag>
                  {' - '}
                  <strong>
                    {t(
                      'calendarConnection.appleConnectionSteps.demoLinksOutlook',
                    )}
                  </strong>
                  <StyledAnchorTag
                    display="inline-flex"
                    marginLeft="5px"
                    target="_blank"
                    href={outlookSupportPageUrl}
                  >
                    {t(
                      'calendarConnection.appleConnectionSteps.demoLinksOutlookHere',
                    )}
                    <Redirect />
                  </StyledAnchorTag>
                </div>
              </StepsBlock.Item>
              <StepsBlock.Item>
                <strong>1.</strong>{' '}
                {t('calendarConnection.appleConnectionSteps.stepOne')}{' '}
                <strong>
                  &quot;
                  {t(
                    'calendarConnection.appleConnectionSteps.stepOneSubscribeBold',
                  )}
                  &quot;
                </strong>
                <div style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                    <span style={{ color: '#000', marginRight: '8px' }}>■</span>
                    <strong>
                      {t(
                        'calendarConnection.appleConnectionSteps.stepOneApplePrefix',
                      )}
                    </strong>
                    :{' '}
                    {t(
                      'calendarConnection.appleConnectionSteps.stepOneAppleSuffix',
                    )}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    <span style={{ color: '#000', marginRight: '8px' }}>■</span>
                    <strong>
                      {t(
                        'calendarConnection.appleConnectionSteps.stepOneOutlookPrefix',
                      )}
                    </strong>
                    :{' '}
                    {t(
                      'calendarConnection.appleConnectionSteps.stepOneOutlookSuffix',
                    )}
                  </div>
                </div>
              </StepsBlock.Item>
              <StepsBlock.Item>
                <strong>2.</strong>{' '}
                {t('calendarConnection.appleConnectionSteps.stepTwo')}
              </StepsBlock.Item>
              <StepsBlock.Item>
                <strong>3.</strong>{' '}
                {t('calendarConnection.appleConnectionSteps.stepThree')}
              </StepsBlock.Item>
              <StepsBlock.Item>
                <strong>4.</strong>{' '}
                {t('calendarConnection.appleConnectionSteps.stepFour')}
              </StepsBlock.Item>
            </StepsBlock>
          )}

          {isConnected && (
            <ListWrapper>
              <List>
                <List.Body>
                  {communities?.length > 0 ? (
                    communities.map((item) => {
                      const calendarUrl = getCalendarUrl(item.id);
                      return (
                        <List.Row key={item.id}>
                          <List.Row.Cell isApple={true}>
                            <CheckBox
                              isSelected={syncAppleFieldProps.value.includes(
                                item.id,
                              )}
                              name={FIELDS.SYNC_EVENTS_APPLE}
                              reduceMargin
                              onChange={() => {
                                onCheckBoxChange(item.id);
                              }}
                              width="20px"
                              checkBoxWidth="20px"
                              disabled={settingsSaving}
                            />
                          </List.Row.Cell>
                          <List.Row.Cell isApple={true}>
                            <LogoWithTitle
                              logo={item.logo}
                              title={item.name}
                              imageWidth="40px"
                              showTooltip
                              comminuteTitleStyle={comminuteTitleStyle}
                            />
                          </List.Row.Cell>

                          <List.Row.Cell isApple={true}>
                            {calendarUrl ? (
                              <LinkText
                                url={calendarUrl}
                                withCopy
                                copyText={t('common.copyLink')}
                                copiedText={t('common.copied')}
                              />
                            ) : (
                              <span
                                style={{
                                  backgroundColor: '#faf2f2',
                                  padding: '5px',
                                  borderRadius: '5px',
                                }}
                              >
                                {t('calendarConnection.cantSeeICal')} ✔
                              </span>
                            )}
                          </List.Row.Cell>
                          <List.Row.Cell isApple={true}>
                            <StyledAnchorTag
                              target="_blank"
                              href={appleSupportPageUrl}
                              key={item.id}
                            >
                              {t('common.tutorial')}
                              <Redirect />
                            </StyledAnchorTag>
                          </List.Row.Cell>
                        </List.Row>
                      );
                    })
                  ) : (
                    <NoCommunitiesBlock>
                      <UsersIcon />
                      <span>{t('calendarConnection.noCommunities')}</span>
                    </NoCommunitiesBlock>
                  )}
                </List.Body>
              </List>
            </ListWrapper>
          )}
        </CalendarsCard.Row>
      </CalendarsCard>
      <div style={{ height: '100px', minHeight: '100px' }} />
    </>
  );
};

export default ApplceCalendar;
